"use client"

import type React from "react"
import { toPng } from "html-to-image"
import { observer } from "mobx-react-lite"
import { useRef, useState } from "react"

import { Download, Calendar, BarChart3, FileText, TrendingUp, RefreshCw, Mail } from "lucide-react"
import { generateCompanyDashboardPDF } from "../../../shared/functions/scorecard-pdf/GeneratePerformaneAgreementPDF"
import { getCompanyDashboardPDFBase64 } from "../../../shared/apis/service/GeneratePerformaneAgreementPDF"
import { useAppContext } from "../../../shared/functions/Context"
import ErrorBoundary from "../../../shared/components/error-boundary/ErrorBoundary"
import { sendEmail } from "../../../shared/apis/service/emailService"
import { cn } from "../../shared/utils/utils"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu"
import { Card, CardContent } from "./ui/card"
import { Badge } from "./ui/badge"
import { Button } from "./ui/button"
import { AIInsightsPanel } from "../AIInsightsPanel"
import { NoData } from "./NoData"
import { CompanyDashboardQ2 } from "./CompanyDashboardQ2"
import { CompanyDashboardQ4 } from "./CompanyDashboardQ4"

interface TabsProps {
  tab: "Q2" | "Q4"
  setTab: React.Dispatch<React.SetStateAction<"Q2" | "Q4">>
  handleExportPDF: () => Promise<void>
  handleExportExcel: () => Promise<void>
  handleExportDetailedReport: () => Promise<void>
  handleSendEmail: () => Promise<void>
  loading: boolean
}

const Tabs = observer(
  ({
    tab,
    setTab,
    handleExportPDF,
    handleExportExcel,
    handleExportDetailedReport,
    handleSendEmail,
    loading,
  }: TabsProps) => {
    const { store } = useAppContext()
    const [refreshing, setRefreshing] = useState(false)

    const handleRefresh = async () => {
      setRefreshing(true)
      // Simulate refresh - in real app, this would reload data
      setTimeout(() => setRefreshing(false), 1000)
    }

    return (
      <Card className="mb-6 shadow-lg border-0 bg-gradient-to-r from-blue-50 to-indigo-50">
        <CardContent className="pt-6 px-6">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 lg:gap-6">
            <div className="flex items-center space-x-4 flex-wrap gap-2">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-600 rounded-lg">
                  <BarChart3 className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Company Performance Dashboard</h1>
                  <p className="text-sm text-gray-600">Strategic performance management and analytics</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Badge variant="outline" className="text-xs bg-white">
                  <TrendingUp className="w-3 h-3 mr-1" />
                  Live Analytics
                </Badge>
                <Badge variant="outline" className="text-xs bg-white">
                  {store.companyMeasure.all.length} KPIs
                </Badge>
              </div>
            </div>
            <div className="flex items-center space-x-3 flex-wrap gap-2">
              {/* Refresh Button */}
              <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing} className="bg-white">
                <RefreshCw className={cn("w-4 h-4 mr-2", refreshing && "animate-spin")} />
                Refresh
              </Button>
              {/* Export Options */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="bg-white" disabled={loading}>
                    <Download className="w-4 h-4 mr-2" />
                    More 
                    {loading && (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current ml-2"></div>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>More </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleExportPDF}>
                    <FileText className="w-4 h-4 mr-2" />
                    PDF Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleExportExcel}>
                    <FileText className="w-4 h-4 mr-2" />
                    Excel Report
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleExportDetailedReport}>
                    <BarChart3 className="w-4 h-4 mr-2" />
                    Detailed Analysis
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleSendEmail}>
                    <Mail className="w-4 h-4 mr-2" />
                    Send via Email
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              {/* Semester Tabs */}
              <div className="flex bg-white rounded-lg p-1 shadow-sm border">
                <button
                  onClick={() => setTab("Q2")}
                  className={cn(
                    "px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center space-x-2",
                    tab === "Q2"
                      ? "bg-blue-600 text-white shadow-sm"
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-50",
                  )}
                >
                  <Calendar className="w-4 h-4" />
                  <span>Semester 1 (Q2)</span>
                </button>
                <button
                  onClick={() => setTab("Q4")}
                  className={cn(
                    "px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center space-x-2",
                    tab === "Q4"
                      ? "bg-blue-600 text-white shadow-sm"
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-50",
                  )}
                >
                  <Calendar className="w-4 h-4" />
                  <span>Semester 2 (Q4)</span>
                </button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    )
  },
)

export const CompanyDashboard = observer(() => {
  const { ui, store } = useAppContext()
  const [loading, setLoading] = useState(false)
  const [tab, setTab] = useState<"Q2" | "Q4">("Q2")
  const chartref = useRef<HTMLDivElement>(null)
  const [emailStatus, setEmailStatus] = useState<string | null>(null)

  // Get vision and mission from store
  const vision = store.visionmission.all[0]?.asJson.vision || ""
  const mission = store.visionmission.all[0]?.asJson.mission || ""

const handleExportPDF = async () => {
  if (chartref.current === null) return;

  try {
    setLoading(true);

    const dataUrl = await toPng(chartref.current, { cacheBust: true });
    await generateCompanyDashboardPDF(vision, mission, dataUrl);

    ui.snackbar.load({
      id: Date.now(),
      message: "PDF exported successfully!",
      type: "success",
    });
  } catch (error) {
    console.error("Export PDF Error:", error);
    ui.snackbar.load({
      id: Date.now(),
      message: "Error! Failed to export PDF.",
      type: "danger",
    });
  } finally {
    setLoading(false);
  }
};


  // const handleExportPDF = async () => {
  //   if (chartref.current === null) return
  //   try {
  //     setLoading(true)
  //     await toPng(chartref.current, { cacheBust: true }).then(async (dataUrl) => {
  //       const link = document.createElement("a")
  //       link.href = dataUrl
  //       await generateCompanyDashboardPDF(vision, mission, link)
  //     })
  //     ui.snackbar.load({
  //       id: Date.now(),
  //       message: "PDF exported successfully!",
  //       type: "success",
  //     })
  //   } catch (error) {
  //     ui.snackbar.load({
  //       id: Date.now(),
  //       message: "Error! Failed to export PDF.",
  //       type: "danger",
  //     })
  //   }
  //   setLoading(false)
  // }

  const handleExportExcel = async () => {
    setLoading(true)
    try {
      // Simulate Excel export
      await new Promise((resolve) => setTimeout(resolve, 2000))
      ui.snackbar.load({
        id: Date.now(),
        message: "Excel report exported successfully!",
        type: "success",
      })
    } catch (error) {
      ui.snackbar.load({
        id: Date.now(),
        message: "Error! Failed to export Excel.",
        type: "danger",
      })
    }
    setLoading(false)
  }

  const handleExportDetailedReport = async () => {
    setLoading(true)
    try {
      // Simulate detailed report generation
      await new Promise((resolve) => setTimeout(resolve, 3000))
      ui.snackbar.load({
        id: Date.now(),
        message: "Detailed analysis report generated successfully!",
        type: "success",
      })
    } catch (error) {
      ui.snackbar.load({
        id: Date.now(),
        message: "Error! Failed to generate detailed report.",
        type: "danger",
      })
    }
    setLoading(false)
  }

  const handleSendEmail = async () => {
    if (chartref.current === null) return
    setLoading(true)
    try {
      const dataUrl = await toPng(chartref.current, { cacheBust: true })
      const link = document.createElement("a")
      link.href = dataUrl
      const pdfBase64 = await getCompanyDashboardPDFBase64(vision, mission, link)
      // You might want to get the recipient email from a form or user input
      const recipientEmail = "recipient@example.com" // Replace with actual recipient
      const subject = "Company Performance Dashboard PDF"
      const htmlContent = `<p>Dear Recipient,</p><p>Please find attached the Company Performance Dashboard PDF for your review.</p><p>Best regards,</p><p>Your Company</p>`
      await sendEmail(pdfBase64, htmlContent, recipientEmail, subject, "Company_Dashboard.pdf")
      ui.snackbar.load({
        id: Date.now(),
        message: "PDF sent successfully via email!",
        type: "success",
      })
    } catch (error) {
      console.error("Error sending PDF via email:", error)
      ui.snackbar.load({
        id: Date.now(),
        message: "Error! Failed to send PDF via email.",
        type: "danger",
      })
    }
    setLoading(false)
  }

  // Check if there's any data in the store for objectives or measures
  const hasData = store.companyObjective.all.length > 0 || store.companyMeasure.all.length > 0

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30 p-4 sm:p-6 w-full">
        <div className="space-y-6">
          <Tabs
            tab={tab}
            setTab={setTab}
            handleExportPDF={handleExportPDF}
            handleExportExcel={handleExportExcel}
            handleExportDetailedReport={handleExportDetailedReport}
            handleSendEmail={handleSendEmail}
            loading={loading}
          />
          {emailStatus && (
            <div
              className={`mb-4 p-3 rounded-md text-sm ${emailStatus.includes("Failed") ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"}`}
            >
              {emailStatus}
            </div>
          )}
          {!hasData ? (
            <NoData message="No company performance data available. Please add objectives and measures." />
          ) : (
            <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
              {/* Main Dashboard Content */}
              <div className="xl:col-span-3">
                <div ref={chartref} className="space-y-6">
                  {tab === "Q2" && <CompanyDashboardQ2 />}
                  {tab === "Q4" && <CompanyDashboardQ4 />}
                </div>
              </div>
              {/* AI Insights Sidebar */}
              <div className="xl:col-span-1">
                <div className="sticky top-6">
                  <AIInsightsPanel companyId={"Htyghvt76"} />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </ErrorBoundary>
  )
})
